import { imageUrlForKey } from "../stitch-bind";
import { formatPriceCop } from "../price-format";
import { buildWhatsAppProductUrl } from "../whatsapp";
import { categoryDisplayOrder } from "../content/category-order";
import { categoryLabelEs } from "../content/spec-rows";
import { getCollectionBySlug } from "../content/collections";
import { products } from "../content/products";
import type { Product } from "../content/types";

type FilterState = { mode: "all" } | { mode: "category"; category: Product["category"] } | { mode: "collection"; collectionId: string };

function parseInitialFilter(): FilterState {
  const q = new URLSearchParams(window.location.search).get("coleccion");
  if (q) return { mode: "collection", collectionId: q };
  return { mode: "all" };
}

function filterProducts(state: FilterState): readonly Product[] {
  if (state.mode === "all") return products;
  if (state.mode === "category") return products.filter((p) => p.category === state.category);
  return products.filter((p) => p.collectionId === state.collectionId);
}

function presentCategories(): Product["category"][] {
  return categoryDisplayOrder.filter((c) => products.some((p) => p.category === c));
}

function createCategoryCarousel(onSelect: (state: FilterState) => void, initial: FilterState): HTMLElement {
  const shell = document.createElement("div");
  shell.className = "carousel-shell";
  shell.dataset.carouselShell = "";

  const head = document.createElement("div");
  head.className = "carousel-shell__bar";

  const prev = document.createElement("button");
  prev.type = "button";
  prev.className = "carousel-shell__nav-btn";
  prev.dataset.carouselPrev = "";
  prev.setAttribute("aria-label", "Categorías anteriores");
  prev.innerHTML = '<span class="material-symbols-outlined" aria-hidden="true">chevron_left</span>';

  const track = document.createElement("div");
  track.className = "carousel-track carousel-track--chips";
  track.dataset.carouselTrack = "";
  track.setAttribute("role", "tablist");
  track.setAttribute("aria-label", "Categorías de producto");

  const next = document.createElement("button");
  next.type = "button";
  next.className = "carousel-shell__nav-btn";
  next.dataset.carouselNext = "";
  next.setAttribute("aria-label", "Categorías siguientes");
  next.innerHTML = '<span class="material-symbols-outlined" aria-hidden="true">chevron_right</span>';

  const chips: HTMLButtonElement[] = [];

  const addChip = (label: string, filterState: FilterState, active: boolean): void => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "filter-chip carousel-track__item carousel-track__item--chip";
    b.textContent = label;
    b.setAttribute("role", "tab");
    b.setAttribute("aria-selected", active ? "true" : "false");
    if (active) b.classList.add("filter-chip--active");
    b.addEventListener("click", () => {
      for (const c of chips) {
        c.classList.remove("filter-chip--active");
        c.setAttribute("aria-selected", "false");
      }
      b.classList.add("filter-chip--active");
      b.setAttribute("aria-selected", "true");
      if (filterState.mode === "all" || filterState.mode === "category") {
        window.history.replaceState({}, "", window.location.pathname);
      }
      onSelect(filterState);
    });
    chips.push(b);
    track.appendChild(b);
  };

  addChip("Todo", { mode: "all" }, initial.mode === "all");

  for (const cat of presentCategories()) {
    const active = initial.mode === "category" && initial.category === cat;
    addChip(categoryLabelEs(cat), { mode: "category", category: cat }, active);
  }

  head.appendChild(prev);
  head.appendChild(track);
  head.appendChild(next);
  shell.appendChild(head);

  return shell;
}

function createProductGrid(): HTMLElement {
  const grid = document.createElement("div");
  grid.className = "catalog-grid";
  grid.dataset.catalogGrid = "";
  return grid;
}

function renderProductCard(p: Product): HTMLElement {
  const card = document.createElement("article");
  card.className = "catalog-card";

  const link = document.createElement("a");
  link.className = "catalog-card__media-link";
  link.href = `/producto.html?p=${encodeURIComponent(p.slug)}`;

  const figure = document.createElement("figure");
  figure.className = "catalog-card__media";

  const img = document.createElement("img");
  img.src = imageUrlForKey(p.imageKey);
  img.alt = p.name;
  img.width = 800;
  img.height = 1000;
  img.loading = "lazy";
  img.decoding = "async";

  figure.appendChild(img);
  link.appendChild(figure);

  const shade = document.createElement("div");
  shade.className = "catalog-card__shade";
  shade.setAttribute("aria-hidden", "true");

  const meta = document.createElement("div");
  meta.className = "catalog-card__meta";

  const price = document.createElement("span");
  price.className = "catalog-card__price";
  price.textContent = formatPriceCop(p.priceCop);

  const name = document.createElement("h2");
  name.className = "catalog-card__name";
  name.textContent = p.name;

  const cat = document.createElement("span");
  cat.className = "catalog-card__category";
  cat.textContent = categoryLabelEs(p.category);

  meta.appendChild(price);
  meta.appendChild(name);
  meta.appendChild(cat);

  const actions = document.createElement("div");
  actions.className = "catalog-card__actions";

  const detail = document.createElement("a");
  detail.href = `/producto.html?p=${encodeURIComponent(p.slug)}`;
  detail.className = "btn btn--secondary catalog-card__btn";
  detail.textContent = "Ver ficha";

  const buy = document.createElement("a");
  buy.href = buildWhatsAppProductUrl(p.name, formatPriceCop(p.priceCop), p.sku);
  buy.target = "_blank";
  buy.rel = "noopener noreferrer";
  buy.className = "btn btn--primary catalog-card__btn";
  buy.textContent = "Comprar";

  actions.appendChild(detail);
  actions.appendChild(buy);

  link.appendChild(shade);
  link.appendChild(meta);

  card.appendChild(link);
  card.appendChild(actions);

  return card;
}

function renderGrid(grid: HTMLElement, list: readonly Product[], note: HTMLElement): void {
  grid.textContent = "";
  note.textContent = `Mostrando ${list.length} pieza${list.length === 1 ? "" : "s"}`;
  for (const p of list) {
    grid.appendChild(renderProductCard(p));
  }
}

export function renderCatalogoPage(): void {
  const root = document.querySelector("[data-catalog-mount]");
  if (!(root instanceof HTMLElement)) return;

  const initial = parseInitialFilter();
  let state: FilterState = initial;

  root.textContent = "";

  const note = document.createElement("p");
  note.className = "body-text page-toolbar__note";
  note.dataset.catalogCount = "";

  const toolbar = document.createElement("div");
  toolbar.className = "page-toolbar page-toolbar--stack";

  const grid = createProductGrid();

  const refresh = (): void => {
    const list = filterProducts(state);
    renderGrid(grid, list, note);
  };

  const carousel = createCategoryCarousel((nextState) => {
    state = nextState;
    refresh();
  }, initial);

  toolbar.appendChild(note);
  if (initial.mode === "collection") {
    const col = getCollectionBySlug(initial.collectionId);
    const hint = document.createElement("p");
    hint.className = "catalog-collection-banner";
    hint.textContent = col
      ? `Estás viendo la línea “${col.name}”. Toca “Todo” o elige otra categoría para ver más productos.`
      : "Línea seleccionada. Toca “Todo” o elige una categoría para ver más productos.";
    toolbar.appendChild(hint);
  }
  toolbar.appendChild(carousel);
  root.appendChild(toolbar);
  root.appendChild(grid);

  if (initial.mode === "collection") {
    state = initial;
  }

  refresh();

  const heroLead = document.querySelector("[data-catalog-hero-lead]");
  if (heroLead && initial.mode === "collection") {
    heroLead.textContent =
      "Aquí solo salen las piezas de esa línea. Mueve el carrusel de categorías arriba o entra sin filtro para ver todo el stock.";
  }
}
